import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BookOpen, EllipsisVertical, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  useToast,
} from '@/components/ui';
import { TypeNameConfirmDialog } from '@/components/ui/TypeNameConfirmDialog';
import { routes } from '@/lib/routes';
import {
  useAllKnowledgeBases,
  useDeleteKnowledgeBase,
} from '@/hooks/useKnowledgeBases';
import { useKbDeepLink } from '@/hooks/useKbDeepLink';
import { KbCreateDialog } from '@/components/knowledge/KbCreateDialog';
import { KbCreateCard } from '@/components/knowledge/KbCreateCard';
import { AuiFiles } from '@/components/aui/components/AuiFiles';
import type { KnowledgeBase } from '@/types/kb';

/**
 * Org-admin KB list. Creation lands at org scope — the dialog's scope
 * is locked, there's no space picker. The page is mounted under
 * `<RequireOrgAdmin>` in App.tsx so non-admins never reach it.
 *
 * Mirrors `SpaceKnowledgeBasesPage` for the inline `AuiFiles` browser
 * experience; the only data difference is the client-side filter
 * (`scope === 'org'` instead of `spaceId === current`).
 */
export default function OrgKnowledgeBasesPage() {
  const toast = useToast();
  const { items: allItems, isLoading, error } = useAllKnowledgeBases();
  const kbs = useMemo(
    () => allItems.filter((kb) => kb.scope === 'org'),
    [allItems],
  );
  const remove = useDeleteKnowledgeBase();

  const params = useParams<{ kbId?: string; fileId?: string }>();
  const selectedKbId = params.kbId ?? null;

  // Two-way bind the selected file to the `/c/org/kbs/:kbId/f/:fileId` URL.
  const deepLink = useKbDeepLink({
    kbId: selectedKbId,
    fileId: params.fileId,
    fileUrl: (fileId) => routes.console.org.knowledgeBaseFile(selectedKbId ?? '', fileId),
    rootUrl: selectedKbId
      ? routes.console.org.knowledgeBase(selectedKbId)
      : routes.console.org.knowledgeBases,
  });

  const selectedKb = useMemo(
    () => kbs.find((kb) => kb.kbId === selectedKbId) ?? null,
    [kbs, selectedKbId],
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<KnowledgeBase | null>(null);

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync({ kbId: pendingDelete.kbId });
      toast.success(`Deleted "${pendingDelete.name}"`);
      setPendingDelete(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Organisation-wide knowledge bases. Every space in the org can
        attach these to its agents.
      </p>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {error.message}
        </div>
      )}

      <KbList
        kbs={kbs}
        isLoading={isLoading}
        selectedKbId={selectedKbId}
        onCreate={() => setCreateOpen(true)}
        onDelete={setPendingDelete}
      />

      {selectedKb && (
        <div className="h-[calc(100vh-22rem)] min-h-[420px]">
          <AuiFiles
            key={selectedKb.kbId}
            ownerId={selectedKb.kbId}
            title={selectedKb.name}
            previewPath={deepLink.previewPath}
            previewKey={deepLink.previewKey}
            onPreviewChange={deepLink.onPreviewChange}
            buildShareUrl={deepLink.buildShareUrl}
          />
        </div>
      )}

      <KbCreateDialog
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        scope="org"
      />

      <TypeNameConfirmDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete knowledge base?"
        message={
          pendingDelete
            ? `Deletes every file in "${pendingDelete.name}" and removes them from the search index. This cannot be undone.`
            : ''
        }
        expectedName={pendingDelete?.name ?? ''}
        isLoading={remove.isPending}
      />
    </div>
  );
}

function KbList({
  kbs,
  isLoading,
  selectedKbId,
  onCreate,
  onDelete,
}: {
  kbs: KnowledgeBase[];
  isLoading: boolean;
  selectedKbId: string | null;
  onCreate: () => void;
  onDelete: (kb: KnowledgeBase) => void;
}) {
  if (isLoading) {
    return (
      <div className="rounded-md border border-border bg-card p-6 text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  return (
    <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {kbs.map((kb) => {
        const isSelected = kb.kbId === selectedKbId;
        return (
          <li key={kb.kbId} className="group relative">
            <Link
              to={routes.console.org.knowledgeBase(kb.kbId)}
              className={
                'flex h-full flex-col gap-2 rounded-md border bg-card p-4 transition-colors ' +
                (isSelected
                  ? 'border-primary-500 ring-1 ring-primary-500/30'
                  : 'border-border hover:bg-accent/20')
              }
            >
              <div className="flex items-start gap-3">
                <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-primary-500" />
                <div className="min-w-0 flex-1">
                  <span className="truncate text-sm font-medium text-foreground">
                    {kb.name}
                  </span>
                </div>
              </div>
              {kb.description && (
                <p className="line-clamp-2 text-xs text-muted-foreground">
                  {kb.description}
                </p>
              )}
            </Link>
            <div
              className="absolute right-2 top-2"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="KB actions"
                    className="rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
                  >
                    <EllipsisVertical className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={4}>
                  <DropdownMenuItem
                    onSelect={() => onDelete(kb)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </li>
        );
      })}
      <KbCreateCard onClick={onCreate} />
    </ul>
  );
}
